import React from 'react';
import PropTypes from 'prop-types';
import '../../styles/timeline.css';
import { useIntl, FormattedMessage } from 'react-intl';
import config from '../../config';
import * as types from '../../lib/constants';
import {
  style,
  SelectExecute,
  TabWrapper,
  Tab,
  TabTitle,
} from './styles/TimeLineToolBarStyles';
import { THUMBNAIL_TITLE_DISPLAY_MODE } from '../../lib/constants';

const { deleteData, restoreDeleteData, completeDeleteData } =
  config.customRole.functionAccess;

/** SelectBoxイベント定義 */
const ExecuteEvent = {
  download: 'download',
  downloadCheckResult: 'downloadCheckResult',
  editTag: 'editTag',
  delete: 'delete',
  restore: 'restore',
  completeDelete: 'completeDelete',
};

const disableButton = (
  <div style={style.disabledButton}>
    <FormattedMessage id="timeLine.timeLineToolBar.execute" />
  </div>
);

const executeButton = (id, onClick) => (
  <div
    style={style.executeButton}
    className="button"
    id={id}
    role="presentation"
    onClick={() => onClick()}
  >
    <FormattedMessage id="timeLine.timeLineToolBar.execute" />
  </div>
);

const createExecuteButton = (
  checkbox,
  executeEvent,
  onDownloadClick,
  onDownloadCheckResultClick,
  onUpdateTagsClick,
  onDataDeleteClick,
  onRestoreClick,
  onCompleteDeleteClick,
) => {
  if (checkbox.ids.length === 0) {
    return disableButton;
  }
  switch (executeEvent) {
    case ExecuteEvent.download:
      return executeButton('timeLineToolBarOnDownloadClick', onDownloadClick);
    case ExecuteEvent.downloadCheckResult:
      return executeButton(
        'timeLineToolBarOnDownloadCheckResultClick',
        onDownloadCheckResultClick,
      );
    case ExecuteEvent.editTag:
      return executeButton(
        'timeLineToolBarOnUpdateTagsClick',
        onUpdateTagsClick,
      );
    case ExecuteEvent.delete:
      return executeButton('timeLineToolBarOnDataDeleteClick', () => {
        const param = {
          disableType: '2',
          workRecordIds: checkbox.ids,
        };
        onDataDeleteClick(param);
      });
    case ExecuteEvent.restore:
      return executeButton('timeLineToolBarOnRestoreClick', () =>
        onRestoreClick(checkbox.ids),
      );
    case ExecuteEvent.completeDelete:
      return executeButton('timeLineToolBarOnCompleteDeleteClick', () =>
        onCompleteDeleteClick(checkbox.ids),
      );
    default:
      return disableButton;
  }
};

const TimeLineToolBar = ({
  innerRole,
  checkbox,
  executeEvent,
  timelines,
  timeLineScreenMode,
  onCheck,
  onUpdateExecuteEvent,
  onDownloadClick,
  onDownloadCheckResultClick,
  onUpdateTagsClick,
  onDataDeleteClick,
  onRestoreClick,
  onCompleteDeleteClick,
  onPostUrlsClick,
  isShowDownload,
  isShowDownloadCheckResult,
  isShowEditTag,
  isShowDelete,
  isShowRestore,
  isShowCompleteDelete,
  provider,
  selectedTab,
  setSelectedTab,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const isDeletable = deleteData.indexOf(innerRole) !== -1;
  const isRestorable = restoreDeleteData.indexOf(innerRole) !== -1;
  const isCompleteDeletable = completeDeleteData.indexOf(innerRole) !== -1;
  const optionLabel = formatMessage({ id: 'timeLine.timeLineToolBar.select' });
  const downloadMsg = formatMessage({
    id: 'timeLine.timeLineToolBar.download',
  });
  const downloadCheckResultMsg = formatMessage({
    id: 'timeLine.timeLineToolBar.download.checkResult',
  });
  const editTagMsg = formatMessage({ id: 'timeLine.timeLineToolBar.editTag' });
  const deleteMsg = formatMessage({ id: 'timeLine.timeLineToolBar.delete' });
  const restoreMsg = formatMessage({ id: 'timeLine.timeLineToolBar.restore' });
  const completeDeleteMsg = formatMessage({
    id: 'timeLine.timeLineToolBar.completeDelete',
  });
  const checkboxValue = 'all';
  const checkedAll =
    (timelines.length > 0 &&
      timelines.length ===
        timelines.filter(x => checkbox.ids.indexOf(x.id) > -1).length) ||
    false;
  const isCansuke = provider && provider.isCansuke;
  const isGridMode =
    timeLineScreenMode &&
    timeLineScreenMode === types.TIMELINE_SCREEN_MODE.grid;

  const isDisplayTab =
    isGridMode && selectedTab !== undefined && selectedTab !== null;
  return (
    <div id="TimeLineToolBar" style={style.main}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div style={{ width: '100%', display: 'flex' }}>
          {isGridMode && (
            <div style={style.allCheckArea}>
              <div
                style={style.checkArea}
                id="timelineCheckArea"
                role="presentation"
                onClick={() => onCheck(checkboxValue, !checkedAll)}
              >
                <div
                  style={style.checkboxArea}
                  id="timelineCheckboxArea"
                  role="presentation"
                  onClick={e => e.stopPropagation()}
                >
                  <input
                    id="timelineCheckbox"
                    style={style.defaultCheckBox}
                    type="checkbox"
                    value={checkboxValue}
                    onChange={e => onCheck(e.target.value, e.target.checked)}
                    checked={checkedAll ? 'checked' : ''}
                  />
                  <label
                    className="timelineCheckboxLabel"
                    style={style.checkboxLabel}
                    htmlFor="timelineCheckbox"
                  />
                </div>
              </div>
              <FormattedMessage id="timeLine.timeLineToolBar.allCheck" />
            </div>
          )}
          {!isCansuke && (
            <div style={style.executeBox}>
              <FormattedMessage id="timeLine.timeLineToolBar.checked" />
              <div style={style.selectBox}>
                <SelectExecute
                  name="pets"
                  id="pet-select"
                  className="selected"
                  onChange={onUpdateExecuteEvent}
                >
                  <option value="">{optionLabel}</option>
                  {isShowDownload && (
                    <option value={ExecuteEvent.download}>{downloadMsg}</option>
                  )}
                  {isShowEditTag && (
                    <option value={ExecuteEvent.editTag}>{editTagMsg}</option>
                  )}
                  {isShowDownloadCheckResult && (
                    <option value={ExecuteEvent.downloadCheckResult}>
                      {downloadCheckResultMsg}
                    </option>
                  )}
                  {isDeletable && isShowDelete && (
                    <option value={ExecuteEvent.delete}>{deleteMsg}</option>
                  )}
                  {isRestorable && isShowRestore && (
                    <option value={ExecuteEvent.restore}>{restoreMsg}</option>
                  )}
                  {isCompleteDeletable && isShowCompleteDelete && (
                    <option value={ExecuteEvent.completeDelete}>
                      {completeDeleteMsg}
                    </option>
                  )}
                </SelectExecute>
              </div>
              {checkbox &&
                createExecuteButton(
                  checkbox,
                  executeEvent,
                  onDownloadClick,
                  onDownloadCheckResultClick,
                  onUpdateTagsClick,
                  onDataDeleteClick,
                  onRestoreClick,
                  onCompleteDeleteClick,
                )}
            </div>
          )}
          {isCansuke && checkbox && checkbox.ids.length !== 0 && (
            <div
              style={style.executeButton}
              className="button"
              id="onPostUrlsClick"
              role="presentation"
              onClick={() => onPostUrlsClick()}
            >
              <FormattedMessage id="timeLine.timeLineToolBar.postUrls" />
            </div>
          )}
          {isCansuke && checkbox && checkbox.ids.length === 0 && (
            <div style={style.disabledButton}>
              <FormattedMessage id="timeLine.timeLineToolBar.postUrls" />
            </div>
          )}
        </div>
        {isDisplayTab && (
          <TabWrapper>
            <TabTitle>タイトル表示</TabTitle>
            <Tab
              onClick={() => {
                setSelectedTab(THUMBNAIL_TITLE_DISPLAY_MODE.dateDisplay);
              }}
              active={selectedTab === THUMBNAIL_TITLE_DISPLAY_MODE.dateDisplay}
            >
              日時
            </Tab>
            <Tab
              onClick={() => {
                setSelectedTab(THUMBNAIL_TITLE_DISPLAY_MODE.userDisplay);
              }}
              active={selectedTab === THUMBNAIL_TITLE_DISPLAY_MODE.userDisplay}
            >
              作業者
            </Tab>
            <Tab
              onClick={() => {
                setSelectedTab(THUMBNAIL_TITLE_DISPLAY_MODE.tagDisplay);
              }}
              active={selectedTab === THUMBNAIL_TITLE_DISPLAY_MODE.tagDisplay}
            >
              タグ
            </Tab>
          </TabWrapper>
        )}
      </div>
    </div>
  );
};

TimeLineToolBar.propTypes = {
  innerRole: PropTypes.number.isRequired,
  checkbox: PropTypes.object.isRequired,
  executeEvent: PropTypes.string.isRequired,
  timelines: PropTypes.array.isRequired,
  timeLineScreenMode: PropTypes.number.isRequired,
  onCheck: PropTypes.func.isRequired,
  onUpdateExecuteEvent: PropTypes.func.isRequired,
  onDownloadClick: PropTypes.func,
  onDownloadCheckResultClick: PropTypes.func,
  onUpdateTagsClick: PropTypes.func,
  onDataDeleteClick: PropTypes.func,
  onRestoreClick: PropTypes.func,
  onCompleteDeleteClick: PropTypes.func,
  onPostUrlsClick: PropTypes.func.isRequired,
  isShowDownload: PropTypes.bool,
  isShowDownloadCheckResult: PropTypes.bool,
  isShowEditTag: PropTypes.bool,
  isShowDelete: PropTypes.bool,
  isShowRestore: PropTypes.bool,
  isShowCompleteDelete: PropTypes.bool,
  provider: PropTypes.object.isRequired,
  selectedTab: PropTypes.number,
  setSelectedTab: PropTypes.func,
};

TimeLineToolBar.defaultProps = {
  onDownloadClick: () => {},
  onDownloadCheckResultClick: () => {},
  onUpdateTagsClick: () => {},
  onDataDeleteClick: () => {},
  onRestoreClick: () => {},
  onCompleteDeleteClick: () => {},
  isShowDownload: false,
  isShowDownloadCheckResult: false,
  isShowEditTag: false,
  isShowDelete: false,
  isShowRestore: false,
  isShowCompleteDelete: false,
  selectedTab: null,
  setSelectedTab: () => {},
};

export default TimeLineToolBar;
